import {
  Habit, HabitCompletion, MandalaPrefs, RingState, HabitSlot, CategoryStyle,
  DEFAULT_FESTIVE_MIX, MAX_VISIBLE_RINGS,
  RING_ROTATE_STREAK_THRESHOLD, PETAL_SPIN_STREAK_THRESHOLD,
} from '../challenge.model';
import { streakToTier } from './tier';

function shiftDate(date: string, deltaDays: number): string {
  const d = new Date(date + 'T00:00:00');
  d.setDate(d.getDate() + deltaDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function categoryStreak(category: string, habits: Habit[], completions: HabitCompletion[], today: string): number {
  const habitIds = new Set(habits.filter(h => h.category === category && !h.archivedAt).map(h => h.id));
  if (habitIds.size === 0) return 0;
  const datesInCategory = new Set(
    completions.filter(c => habitIds.has(c.habitId)).map(c => c.date)
  );
  let cursor = today;
  if (!datesInCategory.has(cursor)) {
    cursor = shiftDate(cursor, -1);
  }
  let streak = 0;
  while (datesInCategory.has(cursor)) {
    streak++;
    cursor = shiftDate(cursor, -1);
  }
  return streak;
}

function habitSlotsForCategory(
  category: string,
  habits: Habit[],
  completions: HabitCompletion[],
  today: string,
): HabitSlot[] {
  const inCategory = habits
    .filter(h => h.category === category && !h.archivedAt)
    .sort((a, b) => a.createdAt - b.createdAt);
  const completedTodayByHabit = new Set(
    completions.filter(c => c.date === today).map(c => c.habitId)
  );
  return inCategory.map(h => ({
    habitId: h.id,
    habitName: h.name,
    completedToday: completedTodayByHabit.has(h.id),
  }));
}

function last5Days(category: string, habits: Habit[], completions: HabitCompletion[], today: string): boolean[] {
  const habitIds = new Set(habits.filter(h => h.category === category && !h.archivedAt).map(h => h.id));
  const dates = new Set(
    completions.filter(c => habitIds.has(c.habitId)).map(c => c.date)
  );
  const out: boolean[] = [];
  for (let offset = 4; offset >= 0; offset--) {
    out.push(dates.has(shiftDate(today, -offset)));
  }
  return out;
}

function defaultStyleForIndex(i: number): CategoryStyle {
  return DEFAULT_FESTIVE_MIX[i % DEFAULT_FESTIVE_MIX.length];
}

export function computeRingState(
  habits: Habit[],
  completions: HabitCompletion[],
  prefs: MandalaPrefs | null,
  today: string,
): RingState[] {
  // 1. Collect active categories.
  const activeCategoriesSet = new Set<string>();
  for (const h of habits) {
    if (!h.archivedAt && h.category) activeCategoriesSet.add(h.category);
  }
  let activeCategories = Array.from(activeCategoriesSet);

  // 2. Apply visibleCategories filter when above the cap.
  if (activeCategories.length > MAX_VISIBLE_RINGS) {
    const explicit = prefs?.visibleCategories?.filter(c => activeCategoriesSet.has(c));
    if (explicit && explicit.length > 0) {
      activeCategories = explicit.slice(0, MAX_VISIBLE_RINGS);
    } else {
      activeCategories = activeCategories.sort().slice(0, MAX_VISIBLE_RINGS);
    }
  }

  // 3. Apply user ringOrder if present, else alpha-sort for stability.
  if (prefs?.ringOrder?.length) {
    const known = new Set(activeCategories);
    const ordered: string[] = [];
    for (const c of prefs.ringOrder) {
      if (known.has(c)) {
        ordered.push(c);
        known.delete(c);
      }
    }
    for (const c of Array.from(known).sort()) ordered.push(c);
    activeCategories = ordered;
  } else {
    activeCategories = activeCategories.sort();
  }

  // 4. Build a RingState per category.
  return activeCategories.map((category, i) => {
    const userStyle = prefs?.styles?.[category];
    const style = userStyle ?? defaultStyleForIndex(i);
    const streak = categoryStreak(category, habits, completions, today);
    const tier = streakToTier(streak);
    const slots = habitSlotsForCategory(category, habits, completions, today);
    const habitIds = slots.map(s => s.habitId);
    return {
      category,
      habitIds,
      slots,
      streak,
      tier,
      motif: style.motif,
      color: style.color,
      ringRotates: streak >= RING_ROTATE_STREAK_THRESHOLD,
      petalsSpin: streak >= PETAL_SPIN_STREAK_THRESHOLD,
      last5: last5Days(category, habits, completions, today),
      decayedThisLoad: false,
    };
  });
}
